import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useController, useFieldArray, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { HelpCircle } from 'lucide-react';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import ListFieldMobileLabel from '#/modules/commonComponents/form/ListFieldMobileLabel';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import { SelectCore } from '#/modules/commonComponents/form/select';
import { Textarea } from '#/modules/commonComponents/form/Textarea';
import {
  ScanCodeDialog,
  ScanTriggerButton,
} from '#/modules/commonComponents/scan';
import ColumnTipHeader from '#/modules/commonComponents/table/ColumnTipHeader';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import {
  formatUnitQtyFloatingLabel,
  useMaterialUnitOptions,
} from '#/modules/imsMgmt/helper/materialUnit';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import { LINE_REMARK_TEXTAREA_CLASS } from '#/modules/imsMgmt/receipts/shared/lineRemarkTextareaClass';
import {
  getStockNoItemId,
  lookupStockNo,
  renderStockNoAmbiguousRow,
  renderStockNoScannedRow,
  STOCK_NO_SCAN_LABELS,
} from '#/modules/imsMgmt/receipts/shared/scan';
import { fetchWarehouseLocationSelectOptions } from '#/modules/imsMgmt/receipts/stockReceipt/helper/locationSelectOptions';
import InvStockPickerDialog from '#/modules/imsMgmt/receipts/stockTransfer/components/InvStockPickerDialog';
import {
  isFullTransferQty,
  mapInvStockRowToTransferLine,
  maxTransferQtyInUnit,
} from '#/modules/imsMgmt/receipts/stockTransfer/helper/formSerialize';
import { STOCK_NO_POLICY_TIP } from '#/modules/imsMgmt/receipts/stockTransfer/helper/stockNoPolicyTip';
import { STOCK_NO_POLICY_OPTIONS } from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferStatus';
import { TO_WAREHOUSE_TIP } from '#/modules/imsMgmt/receipts/stockTransfer/schemas/stockTransferSchema';
import { lookupQueryKey } from '#/utils/queryKeys';
import { clampQtyToAvailable } from '#/utils/quantityBounds';
import { toast } from '#/utils/toast';

const SAME_WAREHOUSE_SCAN_MESSAGE = '來源倉不得與轉倉倉別相同';

/* eslint-disable max-len -- Tailwind arbitrary grid-cols must stay as one static class string */
/**
 * Header／li 必須同一字串。數量欄 Floating label 顯示本單位上限；表頭僅「數量」避免重複。
 * mins＋gap → 內層 min-w-[80rem]
 */
const TRANSFER_LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[minmax(0,7.5rem)_minmax(10rem,1fr)_minmax(0,7.5rem)_minmax(0,8rem)_14rem_minmax(10rem,11rem)_minmax(9rem,10rem)_minmax(8rem,1fr)_2.5rem]';
/* eslint-enable max-len */

/** overflow-x-auto 內唯一寬度來源；header／li 皆 w-full 吃滿 */
const TRANSFER_LINES_MIN_W = cn(
  'w-full',
  '@3xl/form-section:min-w-[80rem]',
);

const LINE_HEADER_CLASS = cn(
  'hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
  TRANSFER_LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-3 p-3',
  '@3xl/form-section:items-start',
  TRANSFER_LINE_GRID_COLS,
);

/** 手機短欄兩欄；≥@3xl 以 contents 讓子格進表頭橫列 grid */
const MOBILE_FIELD_PAIR_CLASS = cn(
  'grid min-w-0 grid-cols-2 gap-3',
  '@3xl/form-section:contents',
);

const FIELD_CELL_CLASS = 'min-w-0';

/** 唯讀短欄：桌面橫列與同列 Floating／Select 垂直居中（列仍 items-start） */
const READONLY_LINE_CELL_CLASS = cn(
  FIELD_CELL_CLASS,
  '@3xl/form-section:self-center',
);

/** 手機欄位小標；mb-1 與輸入框留距（≥@3xl 隱藏） */
const MOBILE_FIELD_LABEL_CLASS = cn(
  'mb-1 text-xs text-gray-500',
  '@3xl/form-section:hidden',
);

const LineRemoveButton = ({ index, onRemove, ariaLabel }) => (
  <Button
    type="button"
    variant="ghost"
    size="icon-sm"
    className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
    aria-label={ariaLabel}
    onClick={() => onRemove(index)}
  >
    <PiTrashBold className="size-4" />
  </Button>
);

LineRemoveButton.propTypes = {
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

const TransferLineRow = ({
  index,
  control,
  register,
  errors,
  line,
  onRemove,
  locationOptions,
  locationOptionsLoading,
  setValue,
}) => {
  const availableKnown = line.availableQty != null && line.availableQty !== '';
  const availableBase = availableKnown ? Number(line.availableQty) || 0 : 0;
  const mtrId = String(line.mtrId ?? '').trim();
  const qtyId = `lines.${index}.qty`;
  const unitIdField = `lines.${index}.unitId`;
  const toLocationId = `lines.${index}.toLocationId`;
  const stockNoPolicyId = `lines.${index}.stockNoPolicy`;
  const remarkId = `lines.${index}.remark`;

  const initialUnitOptions = Array.isArray(line.unitOptions) && line.unitOptions.length > 0
    ? line.unitOptions
    : null;

  const {
    options: unitOptions,
    selectOptions: unitSelectOptions,
    defaultUnitId,
    isLoading: unitOptionsLoading,
  } = useMaterialUnitOptions({
    mtrId,
    initialOptions: initialUnitOptions,
    enabled: Boolean(mtrId),
  });

  const unitDisabled = !mtrId || unitOptionsLoading;
  const unitError = getNestedError(errors, unitIdField);

  const { field: unitField } = useController({
    name: unitIdField,
    control,
    defaultValue: '',
  });

  const resolvedUnitOptions = (
    Array.isArray(unitOptions) && unitOptions.length > 0
      ? unitOptions
      : (line.unitOptions ?? [])
  );
  const activeUnitId = String(unitField.value || line.unitId || '').trim()
    || defaultUnitId
    || '';
  const qtyMax = availableKnown
    ? maxTransferQtyInUnit(availableBase, resolvedUnitOptions, activeUnitId)
    : 0;
  const qtyClampMax = availableKnown ? Math.max(qtyMax, 1) : null;
  const qty = useWatch({ control, name: qtyId });
  const isFullTransfer = isFullTransferQty(
    qty,
    availableBase,
    resolvedUnitOptions,
    activeUnitId,
  );
  const fromLabel = [line.fromWarehouseLabel, line.fromLocationLabel]
    .filter(Boolean)
    .join(' / ') || '-';
  const qtyFloatingLabel = availableKnown
    ? formatUnitQtyFloatingLabel({
      availableBase,
      qtyMax,
      tone: 'transfer',
    })
    : '可用量未讀到';

  const toLocationError = getNestedError(errors, toLocationId);
  const stockNoPolicyError = getNestedError(errors, stockNoPolicyId);
  const remarkError = getNestedError(errors, remarkId);

  const { field: toLocationField } = useController({
    name: toLocationId,
    control,
    defaultValue: '',
  });

  const { field: stockNoPolicyField } = useController({
    name: stockNoPolicyId,
    control,
    defaultValue: '',
  });

  const removeAriaLabel = `移除明細 ${index + 1}`;

  useEffect(() => {
    if (!mtrId || !defaultUnitId || unitOptionsLoading) {
      return;
    }
    if (String(line.unitId ?? '').trim()) {
      return;
    }
    setValue(unitIdField, defaultUnitId, {
      shouldDirty: true,
      shouldValidate: false,
    });
  }, [
    mtrId,
    defaultUnitId,
    unitOptionsLoading,
    line.unitId,
    unitIdField,
    setValue,
  ]);

  useEffect(() => {
    if (!isFullTransfer) {
      return;
    }
    setValue(stockNoPolicyId, 'TRANSFERRED_KEEP_OLD', {
      shouldDirty: true,
      shouldValidate: false,
    });
  }, [isFullTransfer, stockNoPolicyId, setValue]);

  function handleUnitChange(nextUnitId) {
    unitField.onChange(nextUnitId);
    const max = maxTransferQtyInUnit(availableBase, resolvedUnitOptions, nextUnitId);
    const nextQty = max > 0 ? max : 1;
    setValue(qtyId, nextQty, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (isFullTransferQty(nextQty, availableBase, resolvedUnitOptions, nextUnitId)) {
      setValue(stockNoPolicyId, 'TRANSFERRED_KEEP_OLD', {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }

  return (
    <li className={LINE_ROW_CLASS}>
      <div className="flex min-w-0 items-center justify-between gap-2 @3xl/form-section:hidden">
        <p className="text-sm font-medium tabular-nums text-gray-700">
          {`項目# ${index + 1}.`}
        </p>
        <LineRemoveButton
          index={index}
          onRemove={onRemove}
          ariaLabel={removeAriaLabel}
        />
      </div>
      <div className={READONLY_LINE_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>庫存編號</p>
        <p className="font-mono text-sm text-gray-800">{line.stockNo || '-'}</p>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>物料</p>
        <MaterialNameStack name={line.mtrName} code={line.mtrNo} />
      </div>
      <div className={READONLY_LINE_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>來源倉／儲位</p>
        <p className="truncate text-sm text-gray-800" title={fromLabel}>{fromLabel}</p>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <ListFieldMobileLabel hasError={Boolean(unitError)}>
          單位
        </ListFieldMobileLabel>
        <SelectCore
          id={unitIdField}
          options={unitSelectOptions}
          value={unitField.value}
          onChange={handleUnitChange}
          onBlur={unitField.onBlur}
          isDisabled={unitDisabled}
          noOptionsMessage={
            !mtrId ? '請先選擇庫存' : '此物料尚無可選單位'
          }
          placeholder="單位"
          errors={errors}
          hideErrorMessage
          aria-label={`單位 ${index + 1}`}
          aria-invalid={unitError ? true : undefined}
          title={unitError?.message || undefined}
          className="w-full"
        />
      </div>
      <div className={FIELD_CELL_CLASS}>
        <FloatingInput
          id={qtyId}
          label={qtyFloatingLabel}
          type="number"
          min={1}
          max={qtyMax > 0 ? qtyMax : undefined}
          step={1}
          register={(name) => register(name, {
            onBlur: (event) => {
              let next;
              if (availableKnown) {
                next = clampQtyToAvailable(event.target.value, qtyClampMax);
                next = Math.floor(Number(next)) || 1;
                if (qtyMax > 0) {
                  next = Math.min(next, qtyMax);
                }
              } else {
                const parsed = Math.floor(Number(event.target.value));
                next = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
              }
              setValue(name, next, {
                shouldDirty: true,
                shouldValidate: true,
              });
              if (isFullTransferQty(
                next,
                availableBase,
                resolvedUnitOptions,
                activeUnitId,
              )) {
                setValue(stockNoPolicyId, 'TRANSFERRED_KEEP_OLD', {
                  shouldDirty: true,
                  shouldValidate: false,
                });
              }
            },
          })}
          errors={errors}
          suppressInlineError
          containerClassName="w-full min-w-[13rem]"
        />
      </div>
      <div className={MOBILE_FIELD_PAIR_CLASS}>
        <div className={FIELD_CELL_CLASS}>
          <p className={MOBILE_FIELD_LABEL_CLASS}>儲位</p>
          <SelectCore
            id={toLocationId}
            options={locationOptions}
            value={toLocationField.value}
            onChange={toLocationField.onChange}
            onBlur={toLocationField.onBlur}
            isClearable
            isDisabled={locationOptionsLoading}
            noOptionsMessage="無可用儲位"
            placeholder="可空白"
            errors={errors}
            hideErrorMessage
            aria-label={`目標儲位 ${index + 1}`}
            aria-invalid={toLocationError ? true : undefined}
            title={toLocationError?.message || undefined}
            className="w-full"
          />
        </div>
        <div className={FIELD_CELL_CLASS}>
          <p className={MOBILE_FIELD_LABEL_CLASS}>庫編設定</p>
          <SelectCore
            id={stockNoPolicyId}
            options={STOCK_NO_POLICY_OPTIONS}
            value={stockNoPolicyField.value}
            onChange={stockNoPolicyField.onChange}
            onBlur={stockNoPolicyField.onBlur}
            isDisabled={isFullTransfer}
            errors={errors}
            hideErrorMessage
            aria-label={`庫編設定 ${index + 1}`}
            aria-invalid={stockNoPolicyError ? true : undefined}
            title={stockNoPolicyError?.message || undefined}
            className="w-full"
          />
        </div>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>行備註</p>
        <Textarea
          id={remarkId}
          rows={3}
          aria-label={`行備註 ${index + 1}`}
          aria-invalid={remarkError ? true : undefined}
          title={remarkError?.message || undefined}
          className={LINE_REMARK_TEXTAREA_CLASS}
          {...register(remarkId)}
        />
      </div>
      <div className="hidden min-w-0 @3xl/form-section:flex @3xl/form-section:justify-end @3xl/form-section:pt-2">
        <LineRemoveButton
          index={index}
          onRemove={onRemove}
          ariaLabel={removeAriaLabel}
        />
      </div>
    </li>
  );
};

TransferLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  line: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  locationOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  locationOptionsLoading: PropTypes.bool,
  setValue: PropTypes.func.isRequired,
};

const StockTransferLinesEditor = ({
  control,
  register,
  errors,
  setValue,
  getValues,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  const [stockNoScanOpen, setStockNoScanOpen] = useState(false);
  const [stockNoScanKey, setStockNoScanKey] = useState(0);
  const prevToWarehouseRef = useRef(undefined);

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'lines',
  });

  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });
  const toWarehouseId = useWatch({ control, name: 'toWarehouseId', defaultValue: '' });
  const canPickStock = Boolean(toWarehouseId);

  const locationOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseLocationSelect', { warehouseId: toWarehouseId }),
    queryFn: () => fetchWarehouseLocationSelectOptions(toWarehouseId),
    enabled: Boolean(toWarehouseId),
  });

  const linesForRender = useMemo(() => (
    (fields ?? []).map((field, index) => {
      const watched = watchedLines?.[index] ?? {};
      return {
        ...field,
        ...watched,
        id: field.id,
      };
    })
  ), [fields, watchedLines]);

  const initialSelectedRows = useMemo(
    () => linesForRender
      .filter((line) => line.invStockId)
      .map((line) => ({
        id: line.invStockId,
        stockNo: line.stockNo,
        mtr: {
          mtrNo: line.mtrNo,
          name: line.mtrName,
        },
        warehouse: {
          id: line.fromWarehouseId,
        },
        baseQty: line.availableQty,
      })),
    [linesForRender],
  );

  useEffect(() => {
    const next = toWarehouseId ?? '';
    const prev = prevToWarehouseRef.current;

    if (prev === undefined) {
      prevToWarehouseRef.current = next;
      return;
    }
    if (prev === next) {
      return;
    }

    prevToWarehouseRef.current = next;
    if (prev === '') {
      return;
    }

    const rows = getValues('lines') || [];
    const kept = rows.filter((row) => {
      if (!row.fromWarehouseId) {
        return true;
      }
      return row.fromWarehouseId !== next;
    });

    if (kept.length !== rows.length) {
      replace(kept);
      toast.info('已移除與新轉倉倉別衝突的庫存');
    }

    kept.forEach((_, index) => {
      setValue(`lines.${index}.toLocationId`, '', {
        shouldDirty: true,
        shouldValidate: false,
      });
    });
  }, [toWarehouseId, getValues, replace, setValue]);

  function handleOpenPicker() {
    if (!canPickStock) {
      toast.info(TO_WAREHOUSE_TIP);
      return;
    }
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handleOpenStockNoScan() {
    if (!canPickStock) {
      toast.info(TO_WAREHOUSE_TIP);
      return;
    }
    setStockNoScanKey((prev) => prev + 1);
    setStockNoScanOpen(true);
  }

  function excludeSameWarehouse(row, warehouseId) {
    return Boolean(warehouseId && row?.warehouse?.id === warehouseId);
  }

  async function lookupStockNoForTransfer(code) {
    const result = await lookupStockNo(code);
    const raw = result.items || [];
    const items = raw.filter((row) => !excludeSameWarehouse(row, toWarehouseId));
    if (raw.length > 0 && items.length === 0) {
      throw Object.assign(new Error('same-warehouse'), { code: 'SAME_WAREHOUSE' });
    }
    return { items };
  }

  function handleStockNoScanConfirm(selectedRows) {
    const existingIds = new Set(
      (getValues('lines') || []).map((line) => line.invStockId).filter(Boolean),
    );

    (selectedRows || []).forEach((row) => {
      const id = row?.id;
      if (!id) {
        return;
      }
      if (excludeSameWarehouse(row, toWarehouseId)) {
        toast.error(SAME_WAREHOUSE_SCAN_MESSAGE);
        return;
      }
      if (existingIds.has(id)) {
        toast.error('此庫存已在明細中，已跳過');
        return;
      }
      const line = mapInvStockRowToTransferLine(row);
      if (line) {
        append(line);
        existingIds.add(id);
      }
    });
  }

  function handlePickerConfirm(selectedRows) {
    const existingById = new Map(
      (getValues('lines') || [])
        .filter((line) => line.invStockId)
        .map((line) => [line.invStockId, line]),
    );

    const nextLines = (selectedRows || [])
      .map((row) => {
        const id = row?.id;
        if (!id) {
          return null;
        }
        if (excludeSameWarehouse(row, toWarehouseId)) {
          return null;
        }
        const kept = existingById.get(id);
        if (kept) {
          return kept;
        }
        return mapInvStockRowToTransferLine(row);
      })
      .filter(Boolean);

    replace(nextLines);
  }

  return (
    <section className="@container/form-section">
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-2">
        <h2 className="form-block__title mb-0!">轉倉明細</h2>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="
                p-0 text-gray-400 border-0 bg-transparent inline-flex cursor-help items-center
                hover:text-primary-600
              "
              aria-label="選擇庫存說明"
            >
              <HelpCircle size={14} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {TO_WAREHOUSE_TIP}
          </TooltipContent>
        </Tooltip>
        <Button
          type="button"
          size="sm"
          disabled={!canPickStock}
          onClick={handleOpenPicker}
        >
          選擇庫存
        </Button>
        <ScanTriggerButton
          labels={STOCK_NO_SCAN_LABELS}
          disabled={!canPickStock}
          onClick={handleOpenStockNoScan}
        />
      </div>

      {errors?.lines?.message ? (
        <p className="mb-3 text-sm text-red-500">{errors.lines.message}</p>
      ) : null}

      <div className="overflow-x-auto">
        {linesForRender.length === 0 ? (
          <p className="px-1 py-8 text-center text-sm text-gray-400">
            尚未選擇任何庫存
          </p>
        ) : (
          <div className={TRANSFER_LINES_MIN_W}>
            <div className={LINE_HEADER_CLASS} aria-hidden>
              <span className="min-w-0">庫存編號</span>
              <span className="min-w-0">物料</span>
              <span className="min-w-0">來源倉／儲位</span>
              <span className="min-w-0">單位</span>
              <span className="min-w-0">數量</span>
              <span className="min-w-0">目標儲位（可選）</span>
              <span className="min-w-0">
                <ColumnTipHeader label="庫編設定" tip={STOCK_NO_POLICY_TIP} />
              </span>
              <span className="min-w-0">行備註</span>
              <span className="sr-only min-w-0">操作</span>
            </div>
            <ul className="m-0 list-none divide-y divide-gray-200 p-0">
              {linesForRender.map((line, index) => (
                <TransferLineRow
                  key={line.id}
                  index={index}
                  control={control}
                  register={register}
                  errors={errors}
                  line={line}
                  onRemove={remove}
                  locationOptions={locationOptionsQuery.data ?? []}
                  locationOptionsLoading={locationOptionsQuery.isLoading}
                  setValue={setValue}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      <InvStockPickerDialog
        key={`inv-stock-picker-${pickerKey}`}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        excludeWarehouseId={toWarehouseId || null}
        initialSelectedRows={initialSelectedRows}
        onConfirm={handlePickerConfirm}
      />

      <ScanCodeDialog
        key={`stock-no-scan-${stockNoScanKey}`}
        open={stockNoScanOpen}
        onOpenChange={setStockNoScanOpen}
        labels={STOCK_NO_SCAN_LABELS}
        lookup={lookupStockNoForTransfer}
        getItemId={getStockNoItemId}
        renderScannedRow={renderStockNoScannedRow}
        renderAmbiguousRow={renderStockNoAmbiguousRow}
        onConfirm={handleStockNoScanConfirm}
        getErrorMessage={(error) => (
          error?.code === 'SAME_WAREHOUSE'
            ? SAME_WAREHOUSE_SCAN_MESSAGE
            : (error?.message || '查詢庫存編號失敗')
        )}
      />
    </section>
  );
};

StockTransferLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
};

export default StockTransferLinesEditor;
